import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '../../hooks/useTranslation';

const PricingPlans = ({ showCta = true, compact = false, showHeader = true }) => {
  const { t } = useTranslation();

  const afterTrialTiers = [
    {
      key: 'pro',
      title: t('pricingProTitle'),
      price: '$2.99',
      subtitle: t('pricingAfterTrialProSubtitle'),
      features: [
        t('pricingProFeature1'),
        t('pricingProFeature2'),
        t('pricingProFeature3'),
      ],
    },
    {
      key: 'unlimited',
      title: t('pricingUnlimitedTitle'),
      price: '$7.99',
      subtitle: t('pricingAfterTrialUnlimitedSubtitle'),
      features: [
        t('pricingUnlimitedFeature1'),
        t('pricingUnlimitedFeature2'),
        t('pricingUnlimitedFeature3'),
      ],
    },
  ];

  return (
    <div className={`w-full pricing-plans ${compact ? 'my-4' : 'my-4 sm:my-8'}`}>
      {showHeader && (
        <div className="text-center mb-6 sm:mb-8">
          <h2 className={`${compact ? 'text-xl' : 'text-2xl sm:text-3xl'} font-bold text-gray-900 mb-2`}>
            {t('pricingTitle')}
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto mb-0">
            {t('pricingSubtitle')}
          </p>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative flex flex-col rounded-2xl border border-purple-400 p-6 sm:p-8 text-center bg-white shadow-lg shadow-purple-100 ring-1 ring-purple-200 max-w-xl mx-auto mb-8 sm:mb-10"
      >
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide text-white"
          style={{ background: 'linear-gradient(135deg, #7B2FF7, #9F44FF)' }}
        >
          {t('pricingMostPopular')}
        </span>

        <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-1">{t('pricingFreeTitle')}</h3>
        <p className="text-sm font-semibold text-purple-700 mb-4 leading-snug">{t('pricingTrialOffer')}</p>

        <div className="mb-4">
          <span className="text-4xl font-bold text-gray-900">$0</span>
          <span className="text-sm text-gray-500 ml-2">{t('pricingTrialDuration')}</span>
        </div>

        <ul className="text-left space-y-2 mb-6 list-none p-0 m-0">
          {[t('pricingFreeFeature1'), t('pricingFreeFeature2'), t('pricingFreeFeature3')].map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
              <span className="text-purple-600 font-bold shrink-0">✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {showCta && (
          <Link
            to="/register"
            className="inline-flex items-center justify-center w-full min-h-[48px] px-4 py-3 rounded-xl font-semibold text-sm text-white shadow-md hover:opacity-95 no-underline"
            style={{ background: 'linear-gradient(135deg, #7B2FF7, #9F44FF)' }}
          >
            {t('pricingFreeCta')}
          </Link>
        )}
      </motion.div>

      <div className="text-center mb-5 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('pricingAfterTrialTitle')}</h3>
        <p className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">{t('pricingAfterTrialDesc')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
        {afterTrialTiers.map((tier, index) => (
          <motion.div
            key={tier.key}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className="flex flex-col rounded-2xl border border-gray-200 p-5 sm:p-6 text-center bg-white shadow-sm"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-1">{tier.title}</h4>
            <p className="text-xs font-medium text-purple-700 mb-3">{tier.subtitle}</p>
            <div className="mb-4">
              <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
              <span className="text-sm text-gray-500 ml-1">{t('pricingPerMonth')}</span>
            </div>
            <ul className="text-left space-y-2 flex-1 list-none p-0 m-0">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-purple-600 font-bold shrink-0">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-4 mb-0">{t('pricingAutoAssignedNote')}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PricingPlans;
